"use client";

import { useParams, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import type { DbSiteConfig } from "@/lib/server-db";
import { propertyPublicSlug } from "@/lib/property-slug";
import { publicSitePath } from "@/lib/website/public-path";
import { isPublicUploadUrl } from "@/lib/website/public-image";
import { serializeJsonLd } from "@/lib/website/json-ld";

type DbZone = {
  id: string;
  name: string;
  name_en?: string;
  name_de?: string;
  description?: string;
  description_en?: string;
  description_de?: string;
  images?: string[];
  hookPrice?: number | null;
  propertyCount?: number;
};

type SiteProperty = {
  id: string;
  slug?: string;
  name: string;
  city: string;
  country: string;
  type: string;
  bedrooms: number;
  capacity: number;
  description?: string;
  description_en?: string;
  description_de?: string;
  baseRate?: number;
  photos: string[];
  zoneId?: string;
};

type FeaturedProperty = {
  id: string;
  name: string;
  city: string;
  photos: string[];
  totalPrice?: number | null;
  dateFrom?: string;
  dateTo?: string;
};

export type SiteData = {
  agency: { id: string; name?: string };
  config: DbSiteConfig | null;
  properties: SiteProperty[];
  zones: DbZone[];
  featuredProperties: FeaturedProperty[];
  siteOrigin?: string;
};

const DEFAULT_CONFIG: DbSiteConfig = {
  brandName: "Vilatio",
  template: "mediterranean",
  accentColor: "#2563EB",
  domain: "",
  languages: ["es"],
  content: {
    es: { headline: "Reserva tu estancia perfecta", subheadline: "Alojamientos únicos gestionados con cariño", ctaText: "Ver disponibilidad" },
    en: { headline: "Book your perfect stay", subheadline: "Unique accommodations managed with care", ctaText: "Check availability" },
    de: { headline: "Perfekte Unterkunft buchen", subheadline: "Einzigartige Unterkünfte mit Liebe verwaltet", ctaText: "Verfügbarkeit prüfen" },
  },
  email: "",
  phone: "",
};

type Lang = "es" | "en" | "de";

function BuilderRenderer({ agencyId, config, data, lang }: { agencyId: string; config: DbSiteConfig; data: SiteData; lang: Lang }) {
  const blocks = (config.blocks ?? []).filter((block) => block.enabled);
  const props = data.properties;
  const logoImage = config.logoImage;
  return <div className="site-root" style={{ "--site-accent": config.accentColor } as React.CSSProperties}>
    <a className="site-skip-link" href="#contenido-principal">Saltar al contenido</a>
    <header className="site-header"><div className="site-header-inner"><a aria-label={config.brandName || data.agency.name} className="site-brand" href={publicSitePath(agencyId, "", config.domain)}>{logoImage ? <Image alt={config.brandName || data.agency.name || "Agencia"} className="site-logo-img" width={160} height={40} src={`/api/uploads/${logoImage ?? ""}?w=320`} /> : config.brandName || data.agency.name}</a><nav aria-label="Navegación principal" className="site-nav-links"><a href={publicSitePath(agencyId, "", config.domain)}>Inicio</a><a href="#propiedades">Alojamientos</a><a href="#contacto">Contacto</a></nav></div></header>
    <main id="contenido-principal" tabIndex={-1}>{blocks.map((block) => {
      const title = block.props.title ?? "";
      if (block.type === "spacer") return <div key={block.id} style={{ height: `${Math.max(16, Number(block.props.height ?? 64))}px` }} />;
      if (block.type === "text") return <section className="site-builder-text" key={block.id}><h2>{title}</h2><p>{block.props.text ?? ""}</p></section>;
      if (block.type === "image") { const src = block.props.src ? `/api/uploads/${block.props.src}?w=1200` : "/images/placeholder.svg"; return <section key={block.id} className="site-builder-image"><Image width={1200} height={800} loading="lazy" sizes="100vw" unoptimized={isPublicUploadUrl(src)} src={src} alt={block.props.alt ?? title} /><h2>{title}</h2></section>; }
      if (block.type === "carousel") return <section className="site-builder-carousel" key={block.id}><h2>{title}</h2><div>{props.slice(0, 6).map((property) => { const src = property.photos?.[0] ? `/api/uploads/${property.photos[0]}?w=600` : "/images/placeholder.svg"; return <a key={property.id} href={publicSitePath(agencyId, `/propiedad/${propertyPublicSlug(property)}`, config.domain)}><Image width={600} height={400} sizes="(max-width: 760px) 100vw, 33vw" unoptimized={isPublicUploadUrl(src)} src={src} alt={property.name} /><strong>{property.name}</strong></a>; })}</div></section>;
      if (block.type === "properties") return <section className="site-builder-properties" id="propiedades" key={block.id}><h2>{title}</h2><div>{props.map((property) => <a key={property.id} href={publicSitePath(agencyId, `/propiedad/${propertyPublicSlug(property)}`, config.domain)}><strong>{property.name}</strong><span>{property.city} · desde {property.baseRate ?? "—"} €/noche</span></a>)}</div></section>;
      if (block.type === "contact") return <section className="site-builder-contact" id="contacto" key={block.id}><h2>{title}</h2><p>{block.props.text ?? "Estamos aquí para ayudarte."}</p><a href={`mailto:${config.email}`}>Contactar</a></section>;
      if (block.type === "search") return <section className="site-builder-search" key={block.id}><h2>{title}</h2><form aria-label="Buscar disponibilidad" action={publicSitePath(agencyId, "/buscar", config.domain)} method="get"><input aria-label="Fecha de entrada" name="checkin" type="date" required /><input aria-label="Fecha de salida" name="checkout" type="date" required /><input aria-label="Número de huéspedes" min="1" name="guests" placeholder="Huéspedes" type="number" /><button type="submit">Buscar disponibilidad</button></form></section>;
      return <section className="site-builder-hero" key={block.id}><h1>{title || config.content?.[lang]?.headline}</h1><p>{block.props.text || config.content?.[lang]?.subheadline}</p></section>;
    })}</main><footer className="site-builder-footer">{config.brandName || data.agency.name} · Powered by Vilatio</footer>
  </div>;
}

const LABELS: Record<Lang, Record<string, string>> = {
  es: {
    nav_home: "Inicio",
    nav_props: "Propiedades",
    nav_zones: "Zonas",
    nav_contact: "Contacto",
    search_zone: "Zona",
    search_checkin: "Entrada",
    search_checkout: "Salida",
    search_guests: "Huéspedes",
    search_btn: "Buscar",
    all_props: "Todos los alojamientos",
    featured: "🔥 Ofertas destacadas",
    from_price: "desde",
    per_night: "€/noche",
    bedrooms: "dorm.",
    guests: "huésp.",
    no_results: "No se encontraron alojamientos con esos criterios.",
    all_zones: "Todas las zonas",
    contact: "Contacto",
    contact_name: "Tu nombre",
    contact_email: "Tu email",
    contact_message: "Mensaje",
    contact_send: "Enviar mensaje",
    contact_success: "¡Mensaje enviado! Te responderemos pronto.",
    contact_error: "Error al enviar. Inténtalo de nuevo.",
    powered_by: "Powered by",
  },
  en: {
    nav_home: "Home",
    nav_props: "Properties",
    nav_zones: "Areas",
    nav_contact: "Contact",
    search_zone: "Location",
    search_checkin: "Check-in",
    search_checkout: "Check-out",
    search_guests: "Guests",
    search_btn: "Search",
    all_props: "All accommodations",
    featured: "🔥 Featured deals",
    from_price: "from",
    per_night: "€/night",
    bedrooms: "bed.",
    guests: "guests",
    no_results: "No properties found for those criteria.",
    all_zones: "All locations",
    contact: "Contact",
    contact_name: "Your name",
    contact_email: "Your email",
    contact_message: "Message",
    contact_send: "Send message",
    contact_success: "Message sent! We'll get back to you soon.",
    contact_error: "Failed to send. Please try again.",
    powered_by: "Powered by",
  },
  de: {
    nav_home: "Start",
    nav_props: "Unterkünfte",
    nav_zones: "Gebiete",
    nav_contact: "Kontakt",
    search_zone: "Region",
    search_checkin: "Anreise",
    search_checkout: "Abreise",
    search_guests: "Gäste",
    search_btn: "Suchen",
    all_props: "Alle Unterkünfte",
    featured: "🔥 Sonderangebote",
    from_price: "ab",
    per_night: "€/Nacht",
    bedrooms: "Zi.",
    guests: "Gäste",
    no_results: "Keine Unterkünfte für diese Kriterien gefunden.",
    all_zones: "Alle Regionen",
    contact: "Kontakt",
    contact_name: "Dein Name",
    contact_email: "Deine E-Mail",
    contact_message: "Nachricht",
    contact_send: "Nachricht senden",
    contact_success: "Nachricht gesendet! Wir melden uns bald.",
    contact_error: "Fehler beim Senden. Bitte erneut versuchen.",
    powered_by: "Betrieben von",
  },
};

function getLangLabel(zone: DbZone, lang: Lang, field: "name" | "description"): string {
  if (field === "name") {
    if (lang === "en" && zone.name_en) return zone.name_en;
    if (lang === "de" && zone.name_de) return zone.name_de;
    return zone.name;
  }
  if (lang === "en" && zone.description_en) return zone.description_en;
  if (lang === "de" && zone.description_de) return zone.description_de;
  return zone.description ?? "";
}

function getPropDesc(p: SiteProperty, lang: Lang): string | undefined {
  if (lang === "en" && p.description_en) return p.description_en;
  if (lang === "de" && p.description_de) return p.description_de;
  return p.description;
}

function getLangFlag(l: string): string {
  if (l === "es") return "🇪🇸";
  if (l === "en") return "🇬🇧";
  if (l === "de") return "🇩🇪";
  return l.toUpperCase();
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + "T12:00:00");
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

// ── DateRangePicker ──────────────────────────────────────────────────────────

function buildMonthDays(year: number, month: number): (Date | null)[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const offset = (first.getDay() + 6) % 7; // Monday-first
  const days: (Date | null)[] = Array(offset).fill(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  while (days.length % 7 !== 0) days.push(null);
  return days;
}

function isoStr(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function todayIso() { return isoStr(new Date()); }

function DateRangePicker({
  checkin, checkout, onCheckin, onCheckout, t,
}: {
  checkin: string; checkout: string;
  onCheckin: (v: string) => void; onCheckout: (v: string) => void;
  t: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState<"in" | "out">("in");
  const [viewYear, setViewYear] = useState(() => new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => new Date().getMonth());
  const [hovered, setHovered] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  function fmtDisplay(iso: string) {
    if (!iso) return "";
    const d = new Date(iso + "T12:00:00");
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
  }

  function handleDay(iso: string) {
    if (iso < todayIso()) return;
    if (selecting === "in" || !checkin) {
      onCheckin(iso); onCheckout("");
      setSelecting("out");
    } else if (iso <= checkin) {
      onCheckin(iso); onCheckout(""); setSelecting("out");
    } else {
      onCheckout(iso); setSelecting("in"); setOpen(false);
    }
  }

  function dayClass(iso: string): string {
    const past = iso < todayIso();
    const start = iso === checkin;
    const end = iso === checkout;
    const hov = !!(hovered && !checkout && checkin && iso > checkin && iso <= hovered);
    const inRange = !!(checkin && checkout && iso > checkin && iso < checkout);
    return [
      "site-cal-day",
      past ? "is-past" : "",
      start ? "is-start" : "",
      end ? "is-end" : "",
      inRange ? "is-in-range" : "",
      hov ? "is-hover-range" : "",
      iso === todayIso() ? "is-today" : "",
    ].filter(Boolean).join(" ");
  }

  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  const M2 = viewMonth === 11 ? { y: viewYear + 1, m: 0 } : { y: viewYear, m: viewMonth + 1 };
  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  const DAYS = ["L","M","X","J","V","S","D"];

  function renderMonth(year: number, month: number) {
    const days = buildMonthDays(year, month);
    return (
      <div className="site-cal-month">
        <p className="site-cal-month-label">{MONTHS[month]} {year}</p>
        <div className="site-cal-weekdays">{DAYS.map(d => <span key={d}>{d}</span>)}</div>
        <div className="site-cal-grid">
          {days.map((date, i) => {
            if (!date) return <span className="site-cal-empty" key={i} />;
            const iso = isoStr(date);
            return (
              <button
                aria-label={`${date.getDate()} de ${MONTHS[month]} de ${year}`}
                aria-pressed={iso === checkin || iso === checkout}
                className={dayClass(iso)}
                disabled={iso < todayIso()}
                key={i}
                onMouseEnter={() => setHovered(iso)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handleDay(iso)}
                type="button"
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className="site-datepicker-wrap" style={{ display: "flex", alignItems: "stretch", position: "relative" }}>
      <div className="site-search-divider" />
      <div
        aria-label={t.search_checkin}
        className={`site-search-field${open && selecting === "in" ? " is-focused" : ""}`}
        onClick={() => { setOpen(true); setSelecting("in"); }}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setOpen(true); setSelecting("in"); } }}
        role="button"
        style={{ cursor: "pointer" }}
        tabIndex={0}
      >
        <label>{t.search_checkin}</label>
        <span className={`site-search-date-display${!checkin ? " is-placeholder" : ""}`}>
          {checkin ? fmtDisplay(checkin) : "Añade fecha"}
        </span>
      </div>
      <div className="site-search-divider" />
      <div
        aria-label={t.search_checkout}
        className={`site-search-field${open && selecting === "out" ? " is-focused" : ""}`}
        onClick={() => { setOpen(true); setSelecting("out"); }}
        onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); setOpen(true); setSelecting("out"); } }}
        role="button"
        style={{ cursor: "pointer" }}
        tabIndex={0}
      >
        <label>{t.search_checkout}</label>
        <span className={`site-search-date-display${!checkout ? " is-placeholder" : ""}`}>
          {checkout ? fmtDisplay(checkout) : "Añade fecha"}
        </span>
      </div>

      {open && (
        <div aria-label="Seleccionar fechas" className="site-datepicker-popup" role="dialog">
          <div className="site-datepicker-nav">
            <button aria-label="Mes anterior" className="site-cal-nav-btn" onClick={prevMonth} type="button">‹</button>
            <div className="site-cal-months-wrap">
              {renderMonth(viewYear, viewMonth)}
              {renderMonth(M2.y, M2.m)}
            </div>
            <button aria-label="Mes siguiente" className="site-cal-nav-btn" onClick={nextMonth} type="button">›</button>
          </div>
          {(checkin || checkout) && (
            <div className="site-cal-actions">
              <button
                className="site-cal-clear-btn"
                onClick={() => { onCheckin(""); onCheckout(""); setSelecting("in"); }}
                type="button"
              >
                Limpiar fechas
              </button>
              {checkin && checkout && (
                <span className="site-cal-nights">
                  {Math.round((new Date(checkout).getTime() - new Date(checkin).getTime()) / 86400000)} noches
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function AgencySitePage({ initialData }: { initialData?: SiteData }) {
  const { agencyId } = useParams<{ agencyId: string }>();
  const router = useRouter();
  const [data, setData] = useState<SiteData | null>(initialData ?? null);
  const [loading, setLoading] = useState(!initialData);
  const [lang, setLang] = useState<Lang>("es");
  const [searchZone, setSearchZone] = useState("");
  const [searchGuests, setSearchGuests] = useState(1);
  const [checkin, setCheckin] = useState("");
  const [checkout, setCheckout] = useState("");
  const [zoneOpen, setZoneOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactStatus, setContactStatus] = useState<"idle" | "sending" | "success" | "error">("idle");

  const heroRef = useRef<HTMLElement>(null);
  const zonePanelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/api/site/${agencyId}`, { signal: controller.signal }).then((r) => r.json() as Promise<SiteData>).then((d) => {
      setData(d);
      const langs = d.config?.languages ?? ["es"];
      if (langs.length > 0) setLang(langs[0] as Lang);
      setLoading(false);
    }).catch((error: unknown) => {
      if (error instanceof DOMException && error.name === "AbortError") return;
      setLoading(false);
    });
    return () => controller.abort();
  }, [agencyId]);

  // Change 1: set document.title
  const config = data?.config
    ? {
        ...DEFAULT_CONFIG,
        ...data.config,
        content: {
          ...DEFAULT_CONFIG.content,
          ...(data.config.content ?? {}),
        },
      }
    : DEFAULT_CONFIG;
  const content = data
    ? (config.content as Record<string, { headline: string; subheadline: string; ctaText: string }>)[lang] ?? config.content.es
    : undefined;

  useEffect(() => {
    if (data?.config?.brandName || data?.agency?.name) {
      document.title = `${data.config?.brandName || data.agency.name} | ${content?.headline || ""}`;
    }
  }, [data, content]);

  useEffect(() => {
    function check() {
      const hero = heroRef.current;
      if (!hero) return;
      // Pill sits 32px from hero bottom (fully inside). Pill height ≈ 64px.
      // Become sticky when pill top would reach top:72px →
      // hero.bottom - 32 - 64 < 72  →  hero.bottom < 168
      setIsSticky(hero.getBoundingClientRect().bottom < 168);
    }
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, [loading]);

  useEffect(() => {
    if (!zoneOpen) return;
    function handleClick(e: MouseEvent) {
      if (zonePanelRef.current && !zonePanelRef.current.contains(e.target as Node)) {
        setZoneOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [zoneOpen]);

  async function handleContactSubmit(e: React.FormEvent) {
    e.preventDefault();
    setContactStatus("sending");
    try {
      const res = await fetch(`/api/contact/${agencyId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: contactName, email: contactEmail, message: contactMessage }),
      });
      if (!res.ok) throw new Error();
      setContactStatus("success");
      setContactName("");
      setContactEmail("");
      setContactMessage("");
    } catch {
      setContactStatus("error");
    }
  }

  if (loading) {
    return (
      <div className="site-loading">
        <div className="site-loading-dot" />
      </div>
    );
  }

  if (!data || !data.agency) {
    return <div className="site-not-found">Sitio no encontrado</div>;
  }

  const t = LABELS[lang] ?? LABELS.es;
  const languages = config.languages?.length ? config.languages : ["es"];
  const accent = config.accentColor || "#2563EB";
  const zones = data.zones ?? [];
  const featuredProperties = data.featuredProperties ?? [];

  const zoneNames = zones.length > 0
    ? zones.map((z) => z.name)
    : Array.from(new Set(data.properties.map((p) => p.city).filter(Boolean)));

  const selectedZoneName = searchZone
    ? (zones.find((z) => z.id === searchZone)?.name ?? searchZone)
    : "";

  const filtered = data.properties.filter((p) => {
    if (searchZone) {
      // Filter by zoneId first (new system), fallback to city name match (legacy)
      if (p.zoneId) {
        if (p.zoneId !== searchZone) return false;
      } else {
        const zone = zones.find((z) => z.id === searchZone);
        const matchName = zone ? zone.name : searchZone;
        if (p.city.toLowerCase() !== matchName.toLowerCase()) return false;
      }
    }
    if (searchGuests > 1 && p.capacity < searchGuests) return false;
    return true;
  });

  const baseUrl = config.domain
    ? `https://${config.domain}`
    : (data.siteOrigin ?? (typeof window !== "undefined" ? window.location.origin : ""));

  const agencySchema = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": config.brandName || data.agency.name || "",
    "url": baseUrl,
    ...(config.phone ? { "telephone": config.phone } : {}),
    ...(config.email ? { "email": config.email } : {}),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Alojamientos",
      "itemListElement": data.properties.map((p, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "LodgingBusiness",
          "name": p.name,
          "url": `${baseUrl}${publicSitePath(agencyId, `/propiedad/${propertyPublicSlug(p)}`, config.domain)}`,
          "address": { "@type": "PostalAddress", "addressLocality": p.city, "addressCountry": p.country || "ES" },
          ...(p.baseRate ? { "priceRange": `desde ${p.baseRate}€/noche` } : {}),
        },
      })),
    },
  };

  if (config.blocks?.length) return <><script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(agencySchema) }} /><BuilderRenderer agencyId={agencyId} config={config} data={data} lang={lang} /></>;

  return (
    <div className="site-root" style={{ "--site-accent": accent } as React.CSSProperties}>
      <a className="site-skip-link" href="#contenido-principal">Saltar al contenido</a>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: serializeJsonLd(agencySchema) }} />
      <header className="site-header">
        <div className="site-header-inner">
          <a aria-label={config.brandName || data.agency.name} className="site-brand" href={publicSitePath(agencyId, "", config.domain)}>
            {config.logoImage
               ? <Image alt={config.brandName} className="site-logo-img" width={160} height={40} unoptimized src={`/api/uploads/${config.logoImage}?w=320`} />
              : <span>{config.brandName || data.agency.name}</span>}
          </a>
          <nav aria-label="Navegación principal" className="site-nav-links">
            <a href={publicSitePath(agencyId, "", config.domain)}>{t.nav_home}</a>
            {featuredProperties.length > 0 && <a href="#destacadas">{t.featured.replace(/^.*? /, "")}</a>}
            {zones.length > 0 && <a href="#zonas">{t.nav_zones}</a>}
            <a href="#propiedades">{t.nav_props}</a>
            <a href="#contacto">{t.nav_contact}</a>
          </nav>
          <div className="site-header-right">
            {languages.length > 1 && (
              <div className="site-lang-toggle">
                {languages.map((l) => (
                  <button
                    aria-label={`Cambiar idioma a ${l}`}
                    aria-pressed={lang === l}
                    key={l}
                    className={`site-lang-btn${lang === l ? " is-active" : ""}`}
                    onClick={() => setLang(l as Lang)}
                    type="button"
                  >
                    {getLangFlag(l)}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      <main id="contenido-principal" tabIndex={-1}>
      <section
        ref={heroRef}
        className={`site-hero is-${config.template}`}
      >
        {config.heroImage && (
          <Image
            alt=""
            aria-hidden="true"
            className="site-hero-image"
            fill
            fetchPriority="high"
            preload
            sizes="100vw"
            unoptimized
            src={`/api/uploads/${config.heroImage}?w=1600`}
          />
        )}
        <div className="site-hero-overlay" />
        <div className="site-hero-content">
          <h1 className="site-hero-title">{content?.headline}</h1>
          <p className="site-hero-sub">{content?.subheadline}</p>
        </div>

        <div className={`site-search-wrap${isSticky ? " is-sticky" : ""}`}>
          <div className="site-search-pill" style={{ position: "relative" }}>
            <div className="site-search-field" ref={zonePanelRef} style={{ position: "relative" }}>
              <label>{t.search_zone}</label>
              <button
                aria-expanded={zoneOpen}
                aria-haspopup="listbox"
                aria-label={t.search_zone}
                className="site-search-zone-btn"
                onClick={() => setZoneOpen((v) => !v)}
                type="button"
              >
                {selectedZoneName || t.all_zones}
                <span className="site-search-zone-chevron">▾</span>
              </button>
              {zoneOpen && (
                <div aria-label={t.search_zone} className="site-search-zone-panel" role="listbox">
                  <button
                    aria-selected={!searchZone}
                    className={`site-search-zone-option${!searchZone ? " is-selected" : ""}`}
                    onClick={() => { setSearchZone(""); setZoneOpen(false); }}
                    role="option"
                    type="button"
                  >
                    {t.all_zones}
                  </button>
                  {zones.length > 0
                    ? zones.map((z) => (
                        <button
                          aria-selected={searchZone === z.id}
                          key={z.id}
                          className={`site-search-zone-option${searchZone === z.id ? " is-selected" : ""}`}
                          onClick={() => { setSearchZone(z.id); setZoneOpen(false); }}
                          role="option"
                          type="button"
                        >
                          {getLangLabel(z, lang, "name")}
                        </button>
                      ))
                    : zoneNames.map((name) => (
                        <button
                          aria-selected={searchZone === name}
                          key={name}
                          className={`site-search-zone-option${searchZone === name ? " is-selected" : ""}`}
                          onClick={() => { setSearchZone(name); setZoneOpen(false); }}
                          role="option"
                          type="button"
                        >
                          {name}
                        </button>
                      ))}
                </div>
              )}
            </div>

            {/* Change 6: Custom DateRangePicker */}
            <DateRangePicker
              checkin={checkin}
              checkout={checkout}
              onCheckin={setCheckin}
              onCheckout={setCheckout}
              t={t}
            />

            <div className="site-search-divider" />
            <div className="site-search-field">
              <label>{t.search_guests}</label>
              <div className="site-search-guests">
                <button aria-label="Restar huésped" onClick={() => setSearchGuests((v) => Math.max(1, v - 1))} type="button">−</button>
                <span>{searchGuests}</span>
                <button aria-label="Añadir huésped" onClick={() => setSearchGuests((v) => v + 1)} type="button">+</button>
              </div>
            </div>
            <button
              className="site-search-btn"
              style={{ background: accent }}
              type="button"
              onClick={() => {
                const q = new URLSearchParams({
                  dateFrom: checkin,
                  dateTo: checkout,
                  guests: String(searchGuests),
                  zone: searchZone,
                  lang,
                }).toString();
                  router.push(`${publicSitePath(agencyId, "/buscar", config.domain)}?${q}`);
              }}
            >
              {t.search_btn}
            </button>
          </div>
        </div>

      </section>

      {/* Change 2d: Featured offers with dateFrom/dateTo */}
      {featuredProperties.length > 0 && (
        <section className="site-section" id="destacadas">
          <div className="site-section-inner">
            <h2 className="site-section-title">{t.featured}</h2>
            <div className="site-featured-grid">
              {featuredProperties.map((fp) => {
                const nights = fp.dateFrom && fp.dateTo
                  ? Math.round((new Date(fp.dateTo).getTime() - new Date(fp.dateFrom).getTime()) / 86400000)
                  : 0;
                const dateRange = fp.dateFrom && fp.dateTo
                  ? `${formatShortDate(fp.dateFrom)} – ${formatShortDate(fp.dateTo)}`
                  : null;
                return (
                  <article className="site-featured-card" key={fp.id}>
                    <div className="site-property-photo">
                      {fp.photos.length > 0
                         ? <Image alt={fp.name} loading="lazy" width={800} height={600} sizes="(max-width: 760px) 100vw, 420px" unoptimized src={`/api/uploads/${fp.photos[0]}?w=800`} />
                        : <div className="site-property-no-photo" />}
                    </div>
                    <div className="site-property-info">
                      <h3>{fp.name}</h3>
                      <p className="site-property-meta">{fp.city}</p>
                      {dateRange && <p className="site-featured-date">{dateRange} · {nights} noches</p>}
                      {fp.totalPrice != null && fp.totalPrice > 0 && (
                        <p className="site-featured-price"><strong>{fp.totalPrice.toLocaleString("es-ES")}€</strong> total</p>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Zones as cards */}
      {zones.length > 0 && (
        <section className="site-section" id="zonas">
          <div className="site-section-inner">
            <h2 className="site-section-title">{t.nav_zones}</h2>
            <div className="site-zone-cards-grid">
              {zones.map((zone) => (
                <a
                  className="site-zone-card"
                  href={publicSitePath(agencyId, `/zona/${zone.id}`, config.domain)}
                  key={zone.id}
                >
                  <div className="site-zone-card-photo">
                    {zone.images?.[0]
                       ? <Image alt={getLangLabel(zone, lang, "name")} width={600} height={400} loading="lazy" sizes="(max-width: 760px) 100vw, 33vw" unoptimized src={`/api/uploads/${zone.images[0]}?w=600`} />
                      : <div className="site-zone-card-no-photo" />}
                  </div>
                  <div className="site-zone-card-info">
                    <h3>{getLangLabel(zone, lang, "name")}</h3>
                    {getLangLabel(zone, lang, "description") && (
                      <p>{getLangLabel(zone, lang, "description")}</p>
                    )}
                    <div className="site-zone-card-meta">
                      {zone.hookPrice != null && (
                        <p className="site-zone-card-price">
                          <span>💰</span>
                          <span>{t.from_price} <strong>{zone.hookPrice.toLocaleString("es-ES")}€/noche</strong></span>
                        </p>
                      )}
                      {(zone.propertyCount ?? 0) > 0 && (
                        <p className="site-zone-card-count">
                          <span>🏠</span>
                          <span>{zone.propertyCount} alojamiento{zone.propertyCount !== 1 ? "s" : ""}</span>
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="site-section" id="propiedades">
        <div className="site-section-inner">
          <h2 className="site-section-title">{t.all_props}</h2>
          {filtered.length === 0 ? (
            <p className="site-no-results">{t.no_results}</p>
          ) : (
            <div className="site-properties-grid">
              {filtered.map((property) => (
                <PropertyCard
                  accent={accent}
                  agencyId={agencyId}
                  content={content}
                  domain={config.domain}
                  key={property.id}
                  lang={lang}
                  property={property}
                  t={t}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="site-contact-section" id="contacto">
        <div className="site-section-inner">
          <h2 className="site-section-title">{t.contact}</h2>
          <div className="site-contact-layout">
            {(config.email || config.phone) && (
              <div className="site-contact-info">
                {config.email && (
                  <a className="site-contact-item" href={`mailto:${config.email}`}>
                    <span className="site-contact-icon">✉</span>
                    {config.email}
                  </a>
                )}
                {config.phone && (
                  <a className="site-contact-item" href={`tel:${config.phone}`}>
                    <span className="site-contact-icon">☎</span>
                    {config.phone}
                  </a>
                )}
              </div>
            )}
            <form className="site-contact-form" onSubmit={handleContactSubmit}>
              <input
                aria-label={t.contact_name}
                className="site-contact-input"
                onChange={(e) => setContactName(e.target.value)}
                placeholder={t.contact_name}
                required
                type="text"
                value={contactName}
              />
              <input
                aria-label={t.contact_email}
                className="site-contact-input"
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder={t.contact_email}
                required
                type="email"
                value={contactEmail}
              />
              <textarea
                aria-label={t.contact_message}
                className="site-contact-input"
                onChange={(e) => setContactMessage(e.target.value)}
                placeholder={t.contact_message}
                required
                rows={4}
                value={contactMessage}
              />
              {contactStatus === "success" ? (
                <p className="site-contact-success">{t.contact_success}</p>
              ) : (
                <>
                  {contactStatus === "error" && (
                    <p className="site-contact-error">{t.contact_error}</p>
                  )}
                  <button
                    className="site-contact-submit"
                    disabled={contactStatus === "sending"}
                    style={{ background: accent }}
                    type="submit"
                  >
                    {contactStatus === "sending" ? "..." : t.contact_send}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      </section>

      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <div className="site-footer-brand">
            <strong>{config.brandName || data.agency.name}</strong>
          </div>
          <nav aria-label="Navegación del pie" className="site-footer-nav">
            <a href={publicSitePath(agencyId, "", config.domain)}>{t.nav_home}</a>
            <a href="#propiedades">{t.nav_props}</a>
            {zones.length > 0 && <a href="#zonas">{t.nav_zones}</a>}
            <a href="#contacto">{t.nav_contact}</a>
          </nav>
          <p className="site-footer-powered">{t.powered_by} <strong>Vilatio</strong></p>
        </div>
      </footer>
    </div>
  );
}

// Change 4a: PropertyCard with agencyId prop, <a> wrapper
function PropertyCard({
  property,
  lang,
  t,
  accent,
  content,
  agencyId,
  domain,
}: {
  property: SiteProperty;
  lang: Lang;
  t: Record<string, string>;
  accent: string;
  content: { headline: string; subheadline: string; ctaText: string } | undefined;
  agencyId: string;
  domain?: string;
}) {
  const desc = getPropDesc(property, lang);
  return (
    <a className="site-property-card" href={publicSitePath(agencyId, `/propiedad/${propertyPublicSlug(property)}`, domain)} style={{ textDecoration: "none", color: "inherit" }}>
      <div className="site-property-photo">
        {property.photos.length > 0 ? (
          <Image alt={property.name} loading="lazy" width={800} height={600} sizes="(max-width: 760px) 100vw, 420px" unoptimized src={`/api/uploads/${property.photos[0]}?w=800`} />
        ) : (
          <div className="site-property-no-photo" />
        )}
      </div>
      <div className="site-property-info">
        <h3>{property.name}</h3>
        <p className="site-property-meta">
          {property.city} · {property.bedrooms} {t.bedrooms} · {property.capacity} {t.guests}
        </p>
        {property.baseRate && (
          <p className="site-featured-price">
            {t.from_price} <strong>{property.baseRate}€</strong>{t.per_night}
          </p>
        )}
        {desc && <p className="site-property-desc">{desc}</p>}
        <span
          className="site-property-cta"
          style={{ color: accent, borderColor: accent }}
        >
          {content?.ctaText}
        </span>
      </div>
    </a>
  );
}
